import { Component, OnInit } from '@angular/core';
import { ColDef } from 'ag-grid-enterprise';
import { getDefaultTableColumns } from 'src/app/common/ag-grid-table/default-table-columns';
import { TextCellRendererComponent } from 'src/app/common/ag-grid-table/cell-renderers/render-components/text/text.component';
import { MiscAssetCellRendererComponent } from 'src/app/common/ag-grid-table/cell-renderers/render-components/misc-asset/misc-asset.component';
import { DropdownEditorComponent } from 'src/app/common/ag-grid-table/cell-editors/dropdown/dropdown.component';
import { BooleanEditorComponent } from 'src/app/common/ag-grid-table/cell-editors/boolean/boolean.component';
import { AgGridToolsService } from 'src/app/common/ag-grid-table/services/ag-grid-tools.service';
import { BuildType } from 'src/app/enums/build-type';
import { DynamicMysteryBoxTierLabels } from 'src/app/enums/dynamic-mystery-box-tier';
import { OptionsParams, UtilitiesService } from 'src/app/common/services/utilities.service';

@Component({
  selector: 'app-dynamic-mystery-box-table-ag-grid',
  templateUrl: './dynamic-mystery-box-table-ag-grid.component.html',
  styleUrls: ['./dynamic-mystery-box-table-ag-grid.component.sass']
})
export class DynamicMysteryBoxTableAgGridComponent implements OnInit {

  public entity: string = 'dynamic-mystery-box';
  public columnDefs: ColDef[] = [];
  public bulkUpdateColDefs: any[] = [];
  public columnSelection: string;
  public autopopulateSelect: string = '';
  public buildParams: any = {};
  public tableParams: any = {};

  private options: any = {};

  constructor(
    private utilitiesService: UtilitiesService,
    private agGridToolsService: AgGridToolsService,
  ) {
    this.autopopulateSelect = '';
  }

  async ngOnInit(): Promise<void> {
    await this.setOptions();

    const defaultColumns = getDefaultTableColumns(this.entity, this.agGridToolsService);

    this.columnDefs = [
      ...defaultColumns,
      {
        headerName: 'Name',
        field: 'name',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        cellEditor: 'agTextCellEditor',
        type: 'editableColumn',
      },
      {
        headerName: 'Display Name',
        field: 'displayName',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        cellEditor: 'agTextCellEditor',
        type: 'editableColumn',
      },
      {
        headerName: 'Tier',
        field: 'tier',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        valueFormatter: (params) => DynamicMysteryBoxTierLabels[params.value] || params.value,
        cellEditor: DropdownEditorComponent,
        type: 'editableColumn',
        cellEditorParams: {
          optionValue: 'value',
          optionLabel: 'label',
          options: [
            { label: 'Bronze', value: 1 },
            { label: 'Silver', value: 2 },
            { label: 'Gold', value: 3 }
          ]
        }
      },
      {
        headerName: 'Store Listing',
        field: 'storeListing',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        cellEditor: 'agTextCellEditor',
        type: 'editableColumn',
      },
      {
        headerName: 'Start Date Time',
        field: 'startDateTime',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        type: 'editableColumn',
      },
      {
        headerName: 'End Date Time',
        field: 'endDateTime',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        type: 'editableColumn',
      },
      {
        headerName: 'Close State',
        field: 'closeState',
        cellRenderer: MiscAssetCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          filterKey: 'closeState',
          isAggregate: true,
          fieldKeys: ['name', 'id', 'path'],
          isMiscBuild: true,
          values: this.options.closeState,
          populate: true,
          select: 'id name _id path'
        },
        width: 500,
        autoHeight: true,
        cellEditor: DropdownEditorComponent,
        type: 'editableColumn',
        cellEditorPopup: false,
        cellEditorParams: {
          optionValue: '_id',
          optionLabel: 'name',
          objectValue: true
        },
      },
      {
        headerName: 'Close State Outlined',
        field: 'closeStateOutlined',
        cellRenderer: MiscAssetCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          filterKey: 'closeStateOutlined',
          isAggregate: true,
          fieldKeys: ['name', 'id', 'path'],
          isMiscBuild: true,
          values: this.options.closeStateOutlined,
          populate: true,
          select: 'id name _id path'
        },
        width: 500,
        autoHeight: true,
        cellEditor: DropdownEditorComponent,
        type: 'editableColumn',
        cellEditorPopup: false,
        cellEditorParams: {
          optionValue: '_id',
          optionLabel: 'name',
          objectValue: true
        },
      },
      {
        headerName: 'Display Drop Rates',
        field: 'displayDropRates',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        cellEditor: BooleanEditorComponent,
        type: 'editableColumn',
      },
      {
        headerName: 'Info Button',
        field: 'infoButton',
        cellRenderer: TextCellRendererComponent,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        autoHeight: true,
        cellEditor: BooleanEditorComponent,
        type: 'editableColumn',
      },
    ];

    // Table parameters
    this.tableParams = 
    {
      customActionsLabel: 'Custom Bulk Actions',
      customActionButtons: ['bulkLocalize'],
      localizeCollectionName: 'dynamicmysteryboxes',
    }

    this.bulkUpdateColDefs = [
      {
        name: 'Name', id: 'name', type: 'text'
      },
      {
        name: 'Display Name', id: 'displayName', type: 'text'
      },
      {
        name: 'Tier', id: 'tier', type: 'dropdown',
        options: [
          { label: 'Bronze', value: 1 },
          { label: 'Silver', value: 2 },
          { label: 'Gold', value: 3 }
        ]
      },
      {
        name: 'Start', id: 'start', type: 'date'
      },
      {
        name: 'End', id: 'end', type: 'date'
      },
      {
        name: 'Enabled', id: 'enabled', type: 'boolean', onLabel: 'Enabled', offLabel: 'Disabled'
      },
      {
        name: 'Environment(s)', id: 'env', type: 'multiselect', isFlatList: true
      },
    ];

    this.columnSelection = this.agGridToolsService.getColumnFieldNamesFromCols(this.columnDefs);
  }

  async setOptions() {
    const entities = [
      { ref: 'closeState', entity: 'miscellaneous-build', select: 'id _id name path', query: { entityType: { $in: [BuildType.Levels] } } },
      { ref: 'closeStateOutlined', entity: 'miscellaneous-build', select: 'id _id name path', query: { entityType: { $in: [BuildType.Levels] } } },
    ];

    for (const { ref, entity, select, query } of entities) {
      const params = new OptionsParams({
        entity, select, query
      });
      await this.utilitiesService.getOptions(this.options, ref, params);
    }
  }
}

